import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { LessonDetailStateService } from './lesson-detail-state.service';
import { LessonDetailFormStateService } from './lesson-detail-form-state.service';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces/igrading-template.interface';

export interface IAISummaryGrade {
	categoryName: string;
	gradeName: string;
	rubricDescription: string | null;
}

export interface IAISummaryObjective {
	objectiveName: string;
}

export interface IAISummaryMilestone {
	milestoneName: string;
	description?: string;
}

export interface IAISummaryProjectProgress {
	completedMilestones: IAISummaryMilestone[];
	notes: string;
}

export interface IAISummaryHomework {
	categoryName: string;
	notes: string;
	links: Array<{ name: string; url: string }>;
}

export interface IAISummaryPayload {
	lesson: {
		lessonID: number;
		lessonDate: string;
		lessonType: string;
		language: string;
		languageLevel: string;
		learnerName: string;
		instructorName: string;
	};
	grades: IAISummaryGrade[];
	objectives: IAISummaryObjective[];
	projectProgress: IAISummaryProjectProgress | null;
	homework: IAISummaryHomework | null;
	existingSummary: string | null;
}

interface IAISummaryResponse {
	output: {
		summary: string;
	};
}

@Injectable()
export class LessonSummaryWebhookService {
	private readonly http = inject(HttpClient);
	private readonly config = inject<IAppSettings>(APP_CONFIG);
	private readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);

	generateSummary(): Observable<string> {
		const webhookUrl = this.config.aiSummary?.webhookUrl;
		if (!webhookUrl) {
			throw new Error('AI summary webhook URL is not configured.');
		}

		const payload = this.buildPayload();
		return this.http.post<IAISummaryResponse>(webhookUrl, payload).pipe(map((response) => response?.output?.summary));
	}

	private buildPayload(): IAISummaryPayload {
		const lesson = this.state.lessonRequired();
		const categories = this.state.gradingTemplateCategories();
		const template = this.state.gradingTemplate();
		const objectiveOptions = this.state.objectiveOptions();
		const formValue = this.formState.getFormValue();

		// --- Grades ---
		const grades: IAISummaryGrade[] = categories
			.filter((c) => formValue.categoryGrades[c.gradingTemplateCategoryID] != null)
			.map((c) => {
				const gradeID = formValue.categoryGrades[c.gradingTemplateCategoryID]!;
				const rubric = c.rubrics.find((r) => r.gradeID === gradeID) ?? null;
				const gradeName = this.state.grades().find((g) => g.gradeID === gradeID)?.gradeName ?? String(gradeID);
				return {
					categoryName: c.categoryName,
					gradeName,
					rubricDescription: rubric?.description ?? null
				};
			});

		// --- Objectives ---
		const objectives: IAISummaryObjective[] = formValue.selectedObjectives
			.map((id) => {
				const option = objectiveOptions.find((o) => o.value === id);
				return option ? { objectiveName: String(option.label) } : null;
			})
			.filter((o): o is IAISummaryObjective => o !== null);

		// --- Project Progress ---
		const projectCategory = categories.find((c) => c.categoryType === GradingTemplateCategoryType.Project) ?? null;
		let projectProgress: IAISummaryProjectProgress | null = null;

		if (projectCategory && formValue.completedMilestones.length > 0) {
			const completedSet = new Set(formValue.completedMilestones);
			const milestones: IAISummaryMilestone[] = projectCategory.milestones
				.filter((m) => completedSet.has(m.milestoneID))
				.map((m) => ({ milestoneName: m.milestoneName, description: m.description }));

			projectProgress = {
				completedMilestones: milestones,
				notes: formValue.projectNotes
			};
		}

		// --- Homework ---
		let homework: IAISummaryHomework | null = null;

		// Multi-select homework, D5: category names are comma-joined into the existing single field so
		// the external webhook contract stays unchanged (the feature is pending; revisit the payload
		// shape if it is picked back up).
		if (formValue.homeworkCategoryIDs.length > 0 && template) {
			const selected = new Set(formValue.homeworkCategoryIDs);
			const categoryNames = template.homeworkCategories.filter((c) => selected.has(c.homeworkCategoryID)).map((c) => c.categoryName);
			if (categoryNames.length > 0) {
				homework = {
					categoryName: categoryNames.join(', '),
					notes: formValue.homeworkInstructions,
					links: formValue.homeworkLinks
				};
			}
		}

		// --- Existing Summary ---
		const existingSummary = formValue.summary?.trim() || null;

		return {
			lesson: {
				lessonID: lesson.lessonID,
				lessonDate: lesson.lessonDate,
				lessonType: lesson.lessonTypeName,
				language: lesson.languageName,
				languageLevel: lesson.languageLevel,
				learnerName: lesson.learnerFullName,
				instructorName: lesson.instructorFullName ?? ''
			},
			grades,
			objectives,
			projectProgress,
			homework,
			existingSummary
		};
	}
}
